import {
  Button,
  HorizontalDivider,
  Icon,
  IconButton,
  Modal,
  Typography,
  VerticalDivider
} from '@smartgift/frontend-toolkit';
import { isArray, isEqual } from 'lodash';
import React, { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Table } from '@hero/molecules';
import {
  useGetRecognitionRecipientList,
  useRecogRecipientsBulkUpsert,
  useRecogRecipientsDelete,
  useUpdateRecognition
} from '@hero/services/order-service';
import { useNotification } from '@hero/store/hooks';
import { isEmailValid } from '@hero/utils';
import { removeRecipientSchema } from '@hero/validations';

import * as SS from '../NewContactModal/SpreadSheet/style';

import * as S from './style';

function UploadedEmailModal({
  recognitionCode,
  recognitionMaxCount,
  openModal,
  onRequestClose,
  onStartOver
}) {
  const { t } = useTranslation();

  const [emails, setEmails] = useState([]);
  const [{ pageIndex, pageSize }, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10
  });
  const [globalFilters, setGlobalFilters] = useState({});
  const { showNotification, showSuccess } = useNotification();

  const {
    data: recognitionRecipients,
    refetch: recognitionRecipientsRefetch,
    isLoading
  } = useGetRecognitionRecipientList(
    {
      code: recognitionCode,
      filters: {
        searchTerm: globalFilters?.searchTerm
      },
      pagination: {
        page: pageIndex + 1,
        limit: pageSize
      }
    },
    {
      enabled: !!recognitionCode
    }
  );
  const { mutateAsync: recipientBulkUpsert, isLoading: isBulkUpsertLoading } =
    useRecogRecipientsBulkUpsert();
  const { mutateAsync: recipientDelete, isLoading: isDeleteLoading } = useRecogRecipientsDelete();
  const { mutateAsync: updateRecognition, isLoading: isUpdateRecognitionLoading } =
    useUpdateRecognition();

  const onAddAnEmailClick = () => {
    setEmails([
      { id: 0, hasError: false, email: '' },
      ...emails.map((email, idx) => ({ id: idx + 1, hasError: email.hasError, email: email.email }))
    ]);
  };

  const columns = React.useMemo(
    () => [
      {
        id: 'email',
        header: t('email'),
        editable: true,
        size: 500,
        accessorKey: 'email',
        componentProps: {
          maxLength: 100
        },
        isValidFn: val => isEmailValid(val)
      },
      {
        id: 'actions',
        header: '',
        size: 50,
        cell: ({ row }) => {
          return (
            <IconButton
              onClick={async () => {
                showNotification({
                  title: t('areYouSureRemoveRecipient'),
                  message: t('areYouSureRemoveRecipientMessage', { feature: 'oneTimeGift' }),
                  notificationImage: (
                    <Icon name="error" size="xxl" color="var(--color-error-default)" />
                  ),
                  formData: [
                    {
                      name: 'confirm',
                      title: (
                        <Typography variant="body2Bold" style={{ marginBottom: 4 }}>
                          {t('removeRecipientModalConfirmLabel')}
                        </Typography>
                      ),
                      type: 'text'
                    }
                  ],
                  formValidationSchema: removeRecipientSchema,
                  primaryButtonProps: {
                    children: t('yesRemove'),
                    color: 'error'
                  },
                  secondaryButtonProps: {
                    children: t('cancel')
                  },
                  onSubmit: async () => {
                    try {
                      const email = row.original?.email;
                      if (email) {
                        await recipientDelete({
                          code: recognitionCode,
                          emails: [email]
                        });
                        if (recognitionMaxCount > recognitionRecipients?.totalDocs)
                          await updateRecognition({
                            params: { recognitionCode },
                            body: {
                              contacts: {
                                maxCount: (recognitionRecipients?.totalDocs - 1)?.toString(),
                                type: 'number',
                                ids: []
                              }
                            }
                          });
                        await recognitionRecipientsRefetch();
                      }
                      setEmails(emails => {
                        return emails.filter(email => email.id !== row.id);
                      });
                    } finally {
                      showSuccess({
                        type: 'toast',
                        message: t('successfullyRemovedMessage', { name: row.original.email })
                      });
                    }
                  }
                });
              }}
              disabled={isDeleteLoading}
              variant="text"
              color="secondary"
              style={{ margin: '0 auto' }}
            >
              <Icon name="delete" />
            </IconButton>
          );
        }
      }
    ],
    []
  );

  const updateData = async (originalRow, columnId, value) => {
    if (
      (isArray(value) &&
        !isEqual(
          originalRow[columnId].map?.(col => col.id),
          value.map?.(val => val.value)
        )) ||
      (!isArray(value) && !isEqual(originalRow[columnId], value))
    ) {
      setEmails(emails => {
        return emails?.map(email => {
          if (email.id === originalRow.id) {
            email[columnId] = value;

            email.hasError = !isEmailValid(email.email);

            return email;
          }

          return email;
        });
      });
      await recipientBulkUpsert({
        params: {
          code: recognitionCode
        },
        body: {
          recipients: [
            {
              email: value
            }
          ]
        }
      });

      if (recognitionMaxCount > recognitionRecipients?.totalDocs)
        await updateRecognition({
          params: { recognitionCode },
          body: {
            contacts: {
              maxCount: recognitionRecipients?.totalDocs?.toString(),
              type: 'number',
              ids: []
            }
          }
        });
    }
  };

  useEffect(() => {
    const _emails = recognitionRecipients?.docs.map((item, idx) => ({
      id: idx,
      hasError: false,
      email: item.email
    }));

    setEmails(_emails);
  }, [recognitionRecipients]);

  return (
    <Modal
      isOpen={openModal}
      onRequestClose={onRequestClose}
      title={t('editEmailList')}
      primaryButtonProps={{
        onClick: () => onRequestClose(),
        disabled: isBulkUpsertLoading || isDeleteLoading || isUpdateRecognitionLoading,
        loading: isBulkUpsertLoading || isDeleteLoading || isUpdateRecognitionLoading
      }}
      secondaryButtonProps={{
        onClick: () => onRequestClose(false),
        children: t('cancel')
      }}
    >
      <S.UploadedEmailModal>
        <SS.ReviewHead>
          <SS.ReviewHeadTitle>
            <Typography variant="body1Bold">{t('addOrRemoveEmail')}</Typography>
            <Typography
              color="fg"
              emphasis="weak"
              style={{ display: 'flex', alignItems: 'center', gap: 4 }}
            >
              <Trans
                i18nKey="addOrRemoveEmailDesc"
                components={{
                  button: (
                    <Button variant="link" onClick={onStartOver} color="var(--color-fg-default)">
                      start over
                    </Button>
                  )
                }}
              />
            </Typography>
          </SS.ReviewHeadTitle>
          <Button variant="link" onClick={onStartOver} color="neutral">
            {t('startOver')}
          </Button>
        </SS.ReviewHead>

        <HorizontalDivider $margin="24px 0" />

        <S.Row>
          <S.Col>
            <Typography variant="body2Bold">{t('emailList')}</Typography>
            <VerticalDivider $margin="0 16px" $height="24px" />
            <Typography variant="body2">
              <b>{recognitionRecipients?.totalDocs}</b> {t('emails').toLocaleLowerCase()}
            </Typography>
          </S.Col>
          <Button
            variant="text"
            startIcon={<Icon name="add" size="sm" />}
            onClick={onAddAnEmailClick}
          >
            {t('addAnEmail').toUpperCase()}
          </Button>
        </S.Row>
        <Table
          columns={columns}
          data={emails || []}
          state={{
            setEmails,
            globalFilter: globalFilters
          }}
          updateData={updateData}
          loading={isLoading}
          setPagination={setPagination}
          globalFilters={globalFilters}
          setGlobalFilters={setGlobalFilters}
          useGlobalFuzzyFilter
          manualPagination
          pagination={{
            pageIndex,
            pageSize,
            total: recognitionRecipients?.totalDocs
          }}
          rowHeight={52}
          pageCount={recognitionRecipients?.totalPages}
          enabled={{ filter: false, header: true }}
        />
      </S.UploadedEmailModal>
    </Modal>
  );
}

export { UploadedEmailModal };
